import { useUpdateUserTag } from "hooks/useUpdateUserTag";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TagSelect from "components/TagSelect/TagSelect";
import { UserContextMenu } from "components/views/trainingsManagement/UserContextMenu";
import { Box, CircularProgress, IconButton, Stack, Typography, styled, useTheme } from "@mui/material";
import MUIDataTable, { MUIDataTableColumnDef } from "mui-datatables";
import { UserWorkspaceProfile } from "models/UserWorkspaceProfile";
import { useDeleteManyUsers } from "hooks/userContextMenu/useDeleteManyUsers";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { SendMessageDialog } from "components/views/sendMessage/SendMessageDialog";
import { UserRowDetails } from "components/UserRowDetails";
import { UserRoles } from "constants/userRoles";
import EditIcon from '@mui/icons-material/Edit';
import { UserTagType } from "api/models/UserTagType";
import { createPrefixedPropsFilteredComponent } from "helpers/styledHelpers";
import { MakeSortableHead } from "helpers/sortTableHeaderHelper";

interface UserIdLessonChanged {
    userId: string;
    lessonsChangedAt?: Date;
}

interface UserDataTableProps {
    setParentUsersSelected: Dispatch<SetStateAction<number[]>>;
    users: UserWorkspaceProfile[];
    fetchingUsers: boolean;
    title: string;
    onUserRoleChanged?: (userId: string, newRole: UserRoles) => void;
    isPatronsTable?: boolean;
    usersLessonsChangedAt?: Date;
    expandedRows: string[];
    setExpandedRows: Dispatch<SetStateAction<string[]>>;
}

const StyledMUIDataTable = styled(MUIDataTable)`
  & .tss-1cdcmys-MUIDataTable-responsiveBase {
    overflow: visible !important;
  }
  & .MuiTableCell-head span button {
    font-size: 16px !important;
  }
`;

const StyledUserName = createPrefixedPropsFilteredComponent<{ $color: string; $textAlign: string }>(Typography)(({ $color, $textAlign }) => ({
  color: $color,
  textAlign: $textAlign
}));

const StyledTableHeader = styled('th')({
  textAlign: 'left'
});

const StyledEditTableHeader = styled('th')({
  textAlign: 'center'
});

const StyledActionsContainer = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center'
});

const StyledToolbarStack = styled(Stack)({
  display: 'flex',
  flexDirection: 'row',
                  justifyContent: 'flex-end'
});

export const UsersDataTable = (props: UserDataTableProps) => {
    const [users, setUsers] = useState<UserWorkspaceProfile[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
    const [usersSelected, setUsersSelected] = useState<number[]>([]);
    const [setSelectedRows, setSetSelectedRows] = useState<(rows: number[]) => void>(() => () => {});
    const [usersLessonsChangedAt, setUsersLessonsChangedAt] = useState<UserIdLessonChanged[]>([]);
    const { updateUserTag, updatingUserTag } = useUpdateUserTag();
    const [userTagsA, setUserTagsA] = useState<string[]>([]);
    const [userTagsB, setUserTagsB] = useState<string[]>([]);
    const { deleteManyUsers } = useDeleteManyUsers();
    const { t } = useTranslation();
    const theme = useTheme();
    const { fetchingUsers, setParentUsersSelected, expandedRows, setExpandedRows } = props;

    useEffect(() => {
        const tagsA = new Set<string>();
        const tagsB = new Set<string>();
        users.forEach(u => {
            if (u.tagA) tagsA.add(u.tagA);
            if (u.tagB) tagsB.add(u.tagB);
        });
        setUserTagsA(Array.from(tagsA));
        setUserTagsB(Array.from(tagsB));
    }, [users]);

    useEffect(() => {
        const userDto: UserWorkspaceProfile[] = 
            props.users.map(user => ({
                        id: user.id,
                        role: user.role,
                        email: user.email,
                        displayName: user.displayName || '',
                        tagA: user.tagA || '',
                        tagB: user.tagB || '',
                        hasLessons: user.hasLessons || false,
                        hasSubordinates: user.hasSubordinates || false,
                        emailConfirmed: user.emailConfirmed || false,
                        workspace: user.workspace,
                        patrons: []
                    }));
        setUsers(userDto);
    }, [props.users]);

    useEffect(() => {
        if (props.usersLessonsChangedAt) {
            setUsersLessonsChangedAt(usersSelected.map(u => {
                return { userId: users[u].id, lessonsChangedAt: props.usersLessonsChangedAt }
            }));
        }
    }, [props.usersLessonsChangedAt]);

    const handleUpdateUserTag = async (userId: string, tag: string, tagType: UserTagType) => {
        const resp = await updateUserTag({ userId, newTag: tag, tagType });
        if (resp?.success) {
            setUsers(prevUsers => prevUsers.map(user => {
                if (user.id === userId && user[tagType === UserTagType.A ? 'tagA' : 'tagB'] !== tag) {
                    return { ...user, [tagType === UserTagType.A ? 'tagA' : 'tagB']: tag };
                }
                return user;
            }));

            if (tagType === UserTagType.A && !userTagsA.includes(tag)) {
                setUserTagsA(prevTags => [...prevTags, tag]);
            } else if (tagType === UserTagType.B && !userTagsB.includes(tag)) {
                setUserTagsB(prevTags => [...prevTags, tag]);
            }
        }
    };

    const handleClickOpen = (message: string, action: (userIds: string[]) => void) => {
        setDialogMessage(message);
        setConfirmAction(() => action);
        setDialogOpen(true);
    };

    const handleConfirm = () => {
        confirmAction();
        setDialogOpen(false);
    };

    const handleDeleteRowsClick = (userIdsToDelete: string[], setSelectedRows: (rows: number[]) => void) => {
        handleClickOpen(t('userDataTable.confirmDeleteUsers'), () => handleDeleteManyUsers(userIdsToDelete, setSelectedRows));
    }

    const handleDeleteManyUsers = async (userIds: string[], setSelectedRows: (rows: number[]) => void) => {
        const resp = await deleteManyUsers(userIds);
        if (resp?.success) {
            setUsers(prevUsers => prevUsers.filter(user => !userIds.includes(user.id)));
            setSelectedRows([]);
            setUsersSelected([]);
        }
    }

    const handleDeleteSingleUser = async (userId: string) => {
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        setSelectedRows([]);
    }

    const handleSendEmailsClick = () => {
        setSendMessageDialogOpen(true);
    }

    const roleFilters = useMemo(() => {
        const roles = [t('roles.Admin'), t('roles.Patron'), t('roles.PatronLight'), t('roles.Participant')];
        if (props.isPatronsTable) {
            return roles.filter(r => r !== t('roles.Participant'));
        } else {
            return roles;
        }
    }, [props.isPatronsTable, t]);

    const userColumns = [
        { name: "id", label: "Id", options: { filter: false, sort: false, display: 'excluded' } },
        {
            name: "role",
            label: t('trainingsManagement.roleColumn'),
            options: {
                filter: true,
                sort: true,
                display: 'excluded',
                filterOptions: {
                    names: roleFilters,
                    renderValue: (role: string) => role,
                    logic(role: string, filters: string[]) {
                        return !filters.includes(role);
                    },
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return t(`roles.${value}`);
                }
            }
        },
        {
            name: "email",
            label: '',
            options: {
                filter: false,
                sort: false,
                display: 'excluded'
            }
        },
        {
            name: "displayName",
            label: t('trainingsManagement.nameColumn'),
            options: {
                filter: true,
                sort: true,                
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    const user = users.find(u => u.id === tableMeta.rowData[0]);

                    let color = '';
                    if (user?.emailConfirmed) {
                        const role = (user?.role?.toLowerCase() ?? 'participant') as keyof typeof theme.palette.roles;
                        color = theme.palette.roles[role];
                    } else {
                        color = theme.palette.roles['inactive']
                    }

                    return (
                        <StyledUserName $color={color} $textAlign={'left'}>
                            {value}
                        </StyledUserName>
                    );
                },
                customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
                         return (
                             <StyledTableHeader key={columnMeta.index} onClick={() => _handleToggleColumn(columnMeta.index)}>
                               <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                                 <Typography paddingLeft={theme.spacing(1)} textAlign={'left'} fontWeight={'500'}>{columnMeta.label}</Typography>
                               </MakeSortableHead>
                             </StyledTableHeader>
                         );                        
                },
            }
        },
        {
            name: "tagA",
            label: t('trainingsManagement.tagAColumn'),
            options: {
                filter: true, sort: true,
                filterOptions: {
                    names: [t('usersManagement.tagAemptyFilter'), ...userTagsA],
                    logic(tagA: any, filterVal: any) {
                        const isTagBEmpty = tagA === '' || tagA === undefined || tagA === null;
                        const filterIsEmpty = filterVal.indexOf(t('usersManagement.tagAemptyFilter')) >= 0;
                        const filterByValue = filterVal.indexOf(tagA) >= 0;

                        if (filterIsEmpty && isTagBEmpty) {
                            return false;
                        } else if (filterByValue) {
                            return false;
                        }
                        return true;
                    },
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <TagSelect
                                options={userTagsA}
                                selectedOption={value}
                                onChange={(value: string) => handleUpdateUserTag(tableMeta.rowData[0], value, UserTagType.A)}
                                noOptionsText={t('usersTable.tagsSelectNoRowsText')} />
                        </div>
                    )
                }
            }
        },
        {
            name: "tagB",
            label: t('trainingsManagement.tagBColumn'),
            options: {
                filter: true, sort: true,
                filterOptions: {
                    names: [t('usersManagement.tagBemptyFilter'), ...userTagsB],
                    logic(tagB: any, filterVal: any) {
                        const isTagBEmpty = tagB === '' || tagB === undefined || tagB === null;
                        const filterIsEmpty = filterVal.indexOf(t('usersManagement.tagBemptyFilter')) >= 0;
                        const filterByValue = filterVal.indexOf(tagB) >= 0;

                        if (filterIsEmpty && isTagBEmpty) {
                            return false;
                        } else if (filterByValue) {
                            return false;
                        }
                        return true;
                    },
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <TagSelect
                                options={userTagsB}
                                selectedOption={value}
                                onChange={(value: string) => handleUpdateUserTag(tableMeta.rowData[0], value, UserTagType.B)}
                                noOptionsText={t('usersTable.tagsSelectNoRowsText')} />
                        </div>
                    )
                }
            }
        },
        {
            name: 'actions',
            label: t('trainingsManagement.actions'),
            options: {
                filter: false,
                sort: false,
                display: 'true',
                responsive: 'standard',
                customHeadRender: ({ index, ...columnMeta }: any) => {
                    return (
                        <StyledEditTableHeader key={index}>
                            <EditIcon />
                        </StyledEditTableHeader>
                    );
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <StyledActionsContainer>
                                <UserContextMenu userId={tableMeta.rowData[0]} onUserDelete={handleDeleteSingleUser} />
                            </StyledActionsContainer>
                        </div>
                    )
                }
            }
        },
        {
            name: "hasLessons",
            label: t('trainingsManagement.assignedLessonsColumn'),
            options: {
                filter: true,
                sort: false,
                display: 'excluded',
                filterOptions: {
                    names: [t('trainingsManagement.noLessonsAssignedFilter')],
                    logic(hasLessons: boolean, filterVal: any) {
                        const filterNoLessonsAssigned = filterVal.indexOf(t('trainingsManagement.noLessonsAssignedFilter')) >= 0;

                        if (filterNoLessonsAssigned && !hasLessons) {
                            return false;
                        }
                        return true;
                    },
                },
            }
        },
        {
            name: "hasSubordinates",
            label: t('trainingsManagement.assignedSubordinatesColumn'),
            options: {
                filter: true,
                sort: false,
                display: 'excluded',
                filterOptions: {
                    names: [t('trainingsManagement.noSubordinatesAssignedFilter')],
                    logic(hasSubordinates: boolean, filterVal: any) {
                        const filterNoSubordinatesAssigned = filterVal.indexOf(t('trainingsManagement.noSubordinatesAssignedFilter')) >= 0;

                        if (filterNoSubordinatesAssigned && !hasSubordinates) {
                            return false;
                        }
                        return true;
                    },
                },
            }
        },
        {
            name: "emailConfirmed",
            label: t('trainingsManagement.emailConfirmedColumn'),
            options: {
                filter: true,
                sort: false,
                display: 'excluded',
                filterOptions: {
                    names: [t('trainingsManagement.activeUsersFilter'), t('trainingsManagement.inactiveUsersFilter')],
                    logic(emailConfirmed: boolean, filterVal: any) {
                        const filterActiveUsers = filterVal.indexOf(t('trainingsManagement.activeUsersFilter')) >= 0;
                        const filterInactiveUsers = filterVal.indexOf(t('trainingsManagement.inactiveUsersFilter')) >= 0;

                        if (filterActiveUsers && emailConfirmed) {
                            return false;
                        } else if (filterInactiveUsers && !emailConfirmed) {
                            return false;
                        } else if (!filterActiveUsers && !filterInactiveUsers && emailConfirmed) {
                            return false;
                        }
                        return true;
                    },
                },
            }
        }

    ] as MUIDataTableColumnDef[];

    const handleRowExpansionChange = (curExpandedRows: any[], allExpandedRows: { dataIndex: number }[]) => {
        setExpandedRows((prevExpandedRows) => {
            const clickedRow = allExpandedRows[allExpandedRows.length - 1]?.dataIndex.toString();
    
            if (prevExpandedRows.includes(clickedRow)) {
                return prevExpandedRows.filter((row) => row !== clickedRow);
            } else {
                return [...prevExpandedRows, clickedRow];
            }
        });
    };     
    

    return (
        <Box data-testid={`table-${props.title.toLowerCase()}`}>
            {fetchingUsers ? (
                <CircularProgress />
            ) : (
                <StyledMUIDataTable
                    title={props.title}
                    data={users}
                    columns={userColumns}
                    data-testid={`table-${props.title.toLowerCase()}`}
                    options={{
                        responsive: 'standard',
                        textLabels: {
                            body: {
                              noMatch: t('dataTable.noMatch'),
                              toolTip: t('dataTable.toolTip'),
                              columnHeaderTooltip: column => t('dataTable.columnHeaderTooltip', { column: column.label })
                            },
                            pagination: {
                              next: t('dataTable.next'),
                              previous: t('dataTable.previous'),
                              rowsPerPage: t('dataTable.rowsPerPage'),
                              displayRows: t('dataTable.displayRows'),
                            },
                            toolbar: {
                              search: t('dataTable.search'),
                              downloadCsv: t('dataTable.downloadCsv'),
                              print: t('dataTable.print'),
                              viewColumns: t('dataTable.viewColumns'),
                              filterTable: t('dataTable.filterTable'),
                            },
                            filter: {
                              all: t('dataTable.all'),
                              title: t('dataTable.title'),
                              reset: t('dataTable.reset'),
                            },
                            viewColumns: {
                              title: t('dataTable.viewColumnsTitle'),
                              titleAria: t('dataTable.viewColumnsTitleAria'),
                            },
                            selectedRows: {
                              text: t('dataTable.selectedRowsText'),
                              delete: t('dataTable.delete'),
                              deleteAria: t('dataTable.deleteAria'),
                            },
                        },
                        fixedHeader: false,
                        onRowSelectionChange: (curSelectedRows, allRowsSelected, rowsSelected) => {
                            if (rowsSelected) {
                                setUsersSelected(rowsSelected);
                                setParentUsersSelected(rowsSelected);
                            }
                        },
                        customToolbarSelect: (selectedRows, displayData, setSelectedRowsFunc) => {
                            setSetSelectedRows(() => setSelectedRowsFunc);
                            
                            return (
                            <StyledToolbarStack>
                              <IconButton onClick={handleSendEmailsClick}><MailOutlineIcon /></IconButton>
                              <IconButton onClick={() => handleDeleteRowsClick(selectedRows.data.map((d: any) => users[d.dataIndex].id), setSelectedRowsFunc)}><DeleteIcon /></IconButton>
                            </StyledToolbarStack>
                            )
                        },
                        expandableRows: true,
                        expandableRowsHeader: false,
                        renderExpandableRow: (rowData, rowMeta) => {
                            const colSpan = rowData.length + 1;
                            return (
                                <tr>
                                    <td colSpan={colSpan}>
                                        <UserRowDetails
                                            userId={rowData[0]}
                                            onUserRoleChanged={props.onUserRoleChanged}
                                            userLessonsChangedAt={usersLessonsChangedAt?.find(
                                                (u) => u.userId === rowData[0]
                                            )?.lessonsChangedAt}
                                        />
                                    </td>
                                </tr>
                            );
                        },
                        expandableRowsOnClick: false,
                        rowsExpanded: expandedRows,
                        onRowExpansionChange: handleRowExpansionChange,
                        customSearch: (searchQuery, currentRow, columns) => {
                            let isFound = false;
                            currentRow.forEach((row, index) => {
                            const searchColumnIndexes = [2, 3, 4, 5];
                                if (searchColumnIndexes.includes(index)) {
                                    if (row !== null && row !== undefined && row.toString().toLowerCase().includes(searchQuery.toLowerCase())) {
                                        isFound = true;
                                    }
                                }
                            });
                            return isFound;
                        },
                    }}
                />
            )}
            <ConfirmationDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                handleConfirm={handleConfirm}
                message={dialogMessage}
            />
            <SendMessageDialog
                allUsers={users}
                selectedUsers={usersSelected.map(u => users[u])}
                open={sendMessageDialogOpen}
                onClose={() => setSendMessageDialogOpen(false)}
                onSend={(subject: string, content: string) => {}}
            />
        </Box>
    );    
}